import { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const LEVELS = ["Bachelor", "Master", "Medicine", "Dentistry", "PhD", "Other"];
const VISA = ["Yes", "No", "Not sure"];
const DOCS = ["Yes", "No", "Partially"];

export default function EligibilityForm({ compact = false, testPrefix = "eligibility" }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    study_level: "Bachelor",
    field_of_study: "",
    needs_visa_help: "Not sure",
    start_date: "",
    budget_level: "",
    documents_ready: "No",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await http.post("/leads/eligibility", form);
      setDone(true);
      toast.success("Thank you — we'll review your eligibility and reply soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="card-sp text-center py-12"
        data-testid={`${testPrefix}-form-success`}
      >
        <CheckCircle2 className="w-12 h-12 text-[#D7141A] mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-[#0D1B2A] mb-2">Request received</h3>
        <p className="text-[#4A5568]">
          We'll review your profile and get back to you, usually within 1–2 business days.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`card-sp ${compact ? "p-6" : ""}`}
      data-testid={`${testPrefix}-form`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-sp">Full name *</label>
          <input
            className="input-sp"
            value={form.full_name}
            onChange={change("full_name")}
            required
            data-testid={`${testPrefix}-input-name`}
          />
        </div>
        <div>
          <label className="label-sp">Email *</label>
          <input
            className="input-sp"
            type="email"
            value={form.email}
            onChange={change("email")}
            required
            data-testid={`${testPrefix}-input-email`}
          />
        </div>
        <div>
          <label className="label-sp">Phone / WhatsApp</label>
          <input
            className="input-sp"
            value={form.phone}
            onChange={change("phone")}
            data-testid={`${testPrefix}-input-phone`}
          />
        </div>
        <div>
          <label className="label-sp">Country of residence</label>
          <input
            className="input-sp"
            value={form.country}
            onChange={change("country")}
            data-testid={`${testPrefix}-input-country`}
          />
        </div>
        <div>
          <label className="label-sp">Study level</label>
          <select
            className="input-sp"
            value={form.study_level}
            onChange={change("study_level")}
            data-testid={`${testPrefix}-input-level`}
          >
            {LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label-sp">Field / program of interest</label>
          <input
            className="input-sp"
            value={form.field_of_study}
            onChange={change("field_of_study")}
            data-testid={`${testPrefix}-input-field`}
          />
        </div>
        <div>
          <label className="label-sp">Do you need visa help?</label>
          <select
            className="input-sp"
            value={form.needs_visa_help}
            onChange={change("needs_visa_help")}
            data-testid={`${testPrefix}-input-visa`}
          >
            {VISA.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label-sp">Planned start</label>
          <input
            className="input-sp"
            placeholder="e.g. Oct 2026"
            value={form.start_date}
            onChange={change("start_date")}
            data-testid={`${testPrefix}-input-start`}
          />
        </div>
        <div>
          <label className="label-sp">Approximate budget</label>
          <input
            className="input-sp"
            placeholder="e.g. €4,000 – €8,000 / year"
            value={form.budget_level}
            onChange={change("budget_level")}
            data-testid={`${testPrefix}-input-budget`}
          />
        </div>
        <div>
          <label className="label-sp">Documents ready?</label>
          <select
            className="input-sp"
            value={form.documents_ready}
            onChange={change("documents_ready")}
            data-testid={`${testPrefix}-input-docs`}
          >
            {DOCS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label className="label-sp">Anything else we should know?</label>
        <textarea
          className="input-sp min-h-[90px]"
          value={form.message}
          onChange={change("message")}
          data-testid={`${testPrefix}-input-message`}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary mt-6 w-full justify-center"
        data-testid={`${testPrefix}-submit`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? "Sending…" : "Check My Eligibility"}
      </button>
      <p className="text-xs text-[#718096] mt-3 text-center">
        We reply within 1–2 business days. No spam, ever.
      </p>
    </form>
  );
}
